import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useQuoteContext } from "@/contexts/quote-context";
import * as Sentry from "@sentry/react";

import { trackBeginCheckout, trackCotacaoStepView } from "@/analytics/tracking";
import { toast } from "@/components/ui/use-toast";
import { useMediaQuery } from "@/hooks/use-media-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FaBarcode, FaCreditCard } from "react-icons/fa";
import { FaPix } from "react-icons/fa6";
import BankSlipPayment from "../payment/bank-slip-payment";
import CreditCardForm from "../payment/credit-card-form";
import { paymentFormSchema, PaymentFormValues } from "../payment/payment-types";
import PixPayment from "../payment/pix-payment";
import QuoteCardResume from "../quote-card-resume";
import QuoteCardResumeMobile from "../quote-card-resume-mobile";

interface PaymentInfoProps {
  onNext: () => void;
  onBack: () => void;
}

type PaymentError = Error & {
  rawBackendResponse?: unknown;
  httpStatus?: number;
  needsVerification?: boolean;
};

export function PaymentInfo({ onNext, onBack }: PaymentInfoProps) {
  const {
    quotePrice,
    fetchCupons,
    calculateValue,
    cotacao,
    paymentData,
    clientInfoData,
    payWithCreditCard,
    refetchTransaction,
    transaction,
    persistedPixData,
    persistedBoletoData,
  } = useQuoteContext();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [isOrderSummaryExpanded, setIsOrderSummaryExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedPix, setGeneratedPix] = useState(false);
  const [generatedBoleto, setGeneratedBoleto] = useState(false);
  const hasFetchedCupons = useRef(false);
  const lastTrackedCotacao = useRef<string | null>(null);
  const hasSyncedInitialValue = useRef(false);

  const { paymentMethod } = paymentData || {};

  const defaultValues: Partial<PaymentFormValues> = {
    paymentMethod: paymentMethod || "pix",
    installments: "1",
    card1: {
      name: "",
      number: "",
      expiry: "",
      cvv: "",
      brand: "",
    },
    personalInfo: clientInfoData?.personalInfo ?? {
      name: "",
      cpf: "",
    },
    address: clientInfoData?.address ?? {
      cep: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      mun_codigo: "",
      cep_pesquisado: "",
    },
    termsAccepted: false,
  };

  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues,
    mode: "onSubmit",
  });

  const watchPaymentMethod = form.watch("paymentMethod");

  const onSubmit = async (data: PaymentFormValues) => {
    if (data.paymentMethod === "credit") {
      const cepAtual = form.getValues("address.cep");
      const cepPesquisado = form.getValues("address.cep_pesquisado");

      if (!cepPesquisado || cepAtual !== cepPesquisado) {
        toast({
          title: "CEP não pesquisado",
          description:
            "Você mudou o CEP, por favor clique em 'Pesquisar' novamente.",
          variant: "destructive",
        });
        return;
      }

      setIsSubmitting(true);
      try {
        await payWithCreditCard(data);
        onNext();
      } catch (error: unknown) {
        const paymentError = error as PaymentError;

        if (paymentError?.needsVerification !== false) {
          const faturas = await refetchTransaction();
          const cartaoFatura = faturas.find(
            (f) =>
              !f.por_descricao.toLowerCase().includes("pix") &&
              !f.por_descricao.toLowerCase().includes("boleto") &&
              f.tts_descricao.toLowerCase() !== "vencido",
          );
          if (cartaoFatura) {
            setIsSubmitting(false);
            onNext();
            return;
          }
        }

        const description = paymentError?.message || String(paymentError);
        Sentry.withScope((scope) => {
          scope.setTag("payment_method", "credit_card");
          scope.setContext("payment_error", {
            userMessage: description,
            cotacaoId: cotacao?.cve_codigo,
            clientEmail: cotacao?.ctc_email,
            clientPhone: cotacao?.ctc_telefone_1,
            backendResponse: paymentError.rawBackendResponse,
            httpStatus: paymentError.httpStatus,
          });
          Sentry.captureException(paymentError);
        });
        toast({
          title: "Erro ao processar pagamento",
          description,
          variant: "destructive",
        });
        setIsSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    if (cotacao) calculateValue(cotacao, "pix");
    onBack();
  };

  const handlePaymentMethodChange = (value: string) => {
    calculateValue(cotacao, value as "credit" | "pix" | "bank");
    form.setValue("paymentMethod", value as "credit" | "pix" | "bank");
    form.clearErrors();

    if (value !== "pix" || !persistedPixData) {
      setGeneratedPix(false);
    } else {
      setGeneratedPix(true);
    }

    if (value !== "bank" || !persistedBoletoData) {
      setGeneratedBoleto(false);
    } else {
      setGeneratedBoleto(true);
    }
  };

  const methodesPayment = [
    {
      title: "PIX",
      name: "pix",

      description: "",
      icon: FaPix,
    },
    {
      title: "Boleto bancário",
      name: "bank",
      description: "",
      icon: FaBarcode,
    },
    {
      title: "Cartão de crédito",
      name: "credit",
      description: "parcele em até 12x",
      icon: FaCreditCard,
    },
  ];

  useEffect(() => {
    if (hasFetchedCupons.current) return;
    hasFetchedCupons.current = true;
    fetchCupons();
  }, [fetchCupons]);

  useEffect(() => {
    trackCotacaoStepView("pagamento");
  }, []);

  useEffect(() => {
    if (hasSyncedInitialValue.current || !cotacao) return;
    hasSyncedInitialValue.current = true;
    const method = form.getValues("paymentMethod") as "credit" | "pix" | "bank";
    calculateValue(cotacao, method);
  }, [cotacao, calculateValue, form]);

  useEffect(() => {
    if (!cotacao?.cve_codigo) return;
    if (lastTrackedCotacao.current === cotacao.cve_codigo) return;
    lastTrackedCotacao.current = cotacao.cve_codigo;
    trackBeginCheckout(cotacao);
  }, [cotacao]);

  useEffect(() => {
    if (!transaction || transaction.length === 0 || !cotacao) return;

    const activeInvoice = transaction.find((f) => {
      const status = f.tts_descricao.toLowerCase();
      return (
        status === "a vencer" || status === "avencer" || status === "em aberto"
      );
    });

    if (!activeInvoice) return;

    const paymentDesc = activeInvoice.por_descricao.toLowerCase();
    let method: "credit" | "pix" | "bank" = "credit";

    if (paymentDesc.includes("pix")) {
      method = "pix";
      calculateValue(cotacao, "pix");
    } else if (paymentDesc.includes("boleto")) {
      method = "bank";
      calculateValue(cotacao, "bank");
    } else if (
      paymentDesc.includes("cartão") ||
      paymentDesc.includes("cartao") ||
      paymentDesc.includes("crédito") ||
      paymentDesc.includes("credito")
    ) {
      method = "credit";
      calculateValue(cotacao, "credit");
    }

    form.setValue("paymentMethod", method);
  }, [transaction, cotacao, calculateValue, form]);

  useEffect(() => {
    if (persistedPixData && watchPaymentMethod === "pix") {
      setGeneratedPix(true);
    }
    if (persistedBoletoData && watchPaymentMethod === "bank") {
      setGeneratedBoleto(true);
    }
  }, [watchPaymentMethod, persistedPixData, persistedBoletoData]);

  return (
    <div className="">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-[120px] md:items-start">
        <div className="md:col-span-2">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="bg-n-bg-2 md:p-8 p-4 rounded-[20px] border ">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                  <div className="md:col-span-2">
                    <div className="flex flex-col md:gap-4 gap-2">
                      {methodesPayment.map((methode) => (
                        <div
                          key={methode.name}
                          onClick={() =>
                            handlePaymentMethodChange(methode.name)
                          }
                          className={`border-2 flex items-center transition-colors duration-200 text-n-primary rounded-[20px] min-h-[75px] px-4 py-3 cursor-pointer ${
                            watchPaymentMethod === methode.name
                              ? " bg-n-green-h-light border-n-primary"
                              : "border-gray-200 hover:bg-n-bg-1"
                          }`}
                        >
                          <div className="flex items-center md:gap-4 gap-2">
                            <div
                              className={`flex items-center rounded-full justify-center border p-[2px] h-5 w-5 ${
                                watchPaymentMethod === methode.name
                                  ? "border-n-text-neutral bg-gray-200"
                                  : "border-gray-300 bg-white"
                              }`}
                            >
                              {watchPaymentMethod === methode.name && (
                                <div className="h-2 w-2 bg-n-text-neutral rounded-full fill-primary" />
                              )}
                            </div>
                            <div>
                              <label
                                htmlFor={methode.name}
                                className="flex text-lg items-center gap-2 font-medium cursor-pointer"
                              >
                                <methode.icon className="w-5 h-5 text-n-green" />
                                {methode.title}
                              </label>

                              <span className="text-sm ml-auto text-n-text-neutral italic">
                                {methode.description}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="md:col-span-3 md:border-l md:pl-6">
                    {watchPaymentMethod === "credit" && (
                      <CreditCardForm quotePrice={quotePrice.total} />
                    )}
                    {watchPaymentMethod === "pix" && (
                      <PixPayment
                        setGeneratedPix={setGeneratedPix}
                        generatedPix={generatedPix}
                        onNextStep={onNext}
                      />
                    )}
                    {watchPaymentMethod === "bank" && (
                      <BankSlipPayment
                        quotePrice={quotePrice.total}
                        setGeneratedBoleto={setGeneratedBoleto}
                        generatedBoleto={generatedBoleto}
                        onNextStep={onNext}
                      />
                    )}
                  </div>
                </div>
              </div>

              {generatedBoleto || generatedPix ? (
                <></>
              ) : (
                <div className="flex justify-end mt-6 gap-4">
                  <Button
                    type="button"
                    variant="border"
                    disabled={isSubmitting}
                    onClick={handleBack}
                  >
                    {!isSubmitting && <ArrowLeft />}
                    Voltar
                  </Button>
                  {watchPaymentMethod === "credit" && (
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? "Processando..." : "Finalizar pagamento"}
                      {!isSubmitting && <ArrowRight />}
                    </Button>
                  )}
                </div>
              )}
            </form>
          </Form>
        </div>

        {!isMobile && (
          <div className="md:col-span-1 sticky top-32">
            <QuoteCardResume />
          </div>
        )}
      </div>
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 rounded-t-2xl bg-white border-t shadow-lg z-50">
          <div
            className="p-4 cursor-pointer"
            onClick={() => setIsOrderSummaryExpanded(!isOrderSummaryExpanded)}
          >
            <div className="flex items-center justify-between pb-3 border-b">
              <p className="text-lg text-n-text-green font-bold">Resumo:</p>
              {isOrderSummaryExpanded ? (
                <ChevronDown className="h-5 w-5 text-n-green" />
              ) : (
                <ChevronUp className="h-5 w-5 text-n-green" />
              )}
            </div>
            <div className="flex items-center justify-between mt-2">
              <div>
                <p className="text-sm text-n-text">Total</p>
                <p className="text-xl font-bold text-n-green">
                  R${" "}
                  {quotePrice.total.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {!generatedBoleto ||
                  (!generatedPix && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        form.handleSubmit(onSubmit)();
                      }}
                      disabled={
                        isSubmitting ||
                        (watchPaymentMethod === "pix" && !generatedPix) ||
                        (watchPaymentMethod === "bank" && !generatedBoleto)
                      }
                    >
                      Finalizar
                      <ArrowRight className="ml-2 " />
                    </Button>
                  ))}
              </div>
            </div>
          </div>

          {isOrderSummaryExpanded && (
            <div className="border-t bg-gray-50 max-h-96 overflow-y-auto">
              <QuoteCardResumeMobile />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
